import { Type, Transform, Expose } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument, forceArray, anyToDate } from './Models';
import type { PreferredLanguage } from './User';

export enum TenantPlan {
    // New credit-based plans
    VEILLE = "veille",
    QMS = "qms",
    TD = "td",
    QMS_TD = "qms_td",
    // Legacy plans (kept for migration period)
    FREE = "free",
    FREE_TRY = "free_try",
    EARLY_ACCESS = "early_access",
    EVALUATOR = "evaluator",
    STUDENT = "student",
    MANAGER = "manager",
    UNKNOWN = "unknown",
}

const LEGACY_PLANS = new Set([
    TenantPlan.FREE, TenantPlan.FREE_TRY, TenantPlan.EARLY_ACCESS,
    TenantPlan.EVALUATOR, TenantPlan.STUDENT, TenantPlan.MANAGER,
])

const CREDIT_PLANS = new Set([
    TenantPlan.VEILLE, TenantPlan.QMS, TenantPlan.TD, TenantPlan.QMS_TD,
])

export function isLegacyPlan(plan: TenantPlan | string | null): boolean {
    return LEGACY_PLANS.has(plan as TenantPlan)
}

export function isCreditPlan(plan: TenantPlan | string | null): boolean {
    return CREDIT_PLANS.has(plan as TenantPlan)
}

export enum TenantCustomerPortalUpdateStatus {
    REQUESTED = "requested",
    PENDING = "pending",
    DONE = "done",
    FAILED = "failed",
}

export enum TenantSubscriptionStatus {
    TRIALING = "trialing",
    ACTIVE = "active",
    INCOMPLETE = "incomplete",
    INCOMPLETE_EXPIRED = "incomplete_expired",
    PAST_DUE = "past_due",
    CANCELED = "canceled",
    UNPAID = "unpaid",
    PAUSED = "paused",
}

export enum TenantServiceKey {
    QUOTA_ONE_TIME_ASSESSMENTS = "quota_one_time_assessments",
    QUOTA_RECURRENT_ASSESSMENTS = "quota_recurrent_assessments",
    QUOTA_FREE_TRY_ASSESSMENTS = "quota_free_try_assessments",
    QUOTA_USERS = "quota_users",
    QUOTA_USERS_QMS = "quota_users_qms",
    QUOTA_USERS_TD = "quota_users_td",
    UNKNOWN = "unknown",
}

export interface TenantPlanFeatures {
    has_qms: boolean
    has_td: boolean
    has_veille: boolean
    has_chat: boolean
    max_users: number
    max_projects_qms: number
    max_projects_td: number
    credits_monthly: number
    welcome_bonus_eligible: boolean
}

export interface CreditBalance {
    credits_recurring: number
    credits_topup: number
    credits_recurring_allowance: number
    last_reset_at: Date | null
}

export class Service {
    @Expose()
    public quota: number
    @Expose()
    public quota_remaining: number
}

export class TenantService extends Service {
    @Expose()
    public quota: number = 0
    @Expose()
    public quota_remaining: number = 0
}

export class TenantServices {
    @Expose()
    @Type(() => TenantService)
    public quota_one_time_assessments: TenantService = new TenantService()
    @Expose()
    @Type(() => TenantService)
    public quota_recurrent_assessments: TenantService = new TenantService()
    @Expose()
    @Type(() => TenantService)
    public quota_free_try_assessments: TenantService = new TenantService()
    @Expose()
    @Type(() => TenantService)
    public quota_users: TenantService = new TenantService()
    @Expose()
    @Type(() => TenantService)
    public quota_users_qms: TenantService = new TenantService()
    @Expose()
    @Type(() => TenantService)
    public quota_users_td: TenantService = new TenantService()

    static toPathKey(name: TenantServiceKey): string {
        switch (name) {
            case TenantServiceKey.QUOTA_ONE_TIME_ASSESSMENTS:
                return "quota_one_time_assessments"
            case TenantServiceKey.QUOTA_RECURRENT_ASSESSMENTS:
                return "quota_recurrent_assessments"
            case TenantServiceKey.QUOTA_FREE_TRY_ASSESSMENTS:
                return "quota_free_try_assessments"
            case TenantServiceKey.QUOTA_USERS:
                return "quota_users"
            case TenantServiceKey.QUOTA_USERS_QMS:
                return "quota_users_qms"
            case TenantServiceKey.QUOTA_USERS_TD:
                return "quota_users_td"
            default:
                throw new Error(`Invalid tenant service key: ${name}`)
        }
    }

    public get(name: TenantServiceKey): TenantService | null {
        switch (name) {
            case TenantServiceKey.QUOTA_ONE_TIME_ASSESSMENTS:
                return this.quota_one_time_assessments
            case TenantServiceKey.QUOTA_RECURRENT_ASSESSMENTS:
                return this.quota_recurrent_assessments
            case TenantServiceKey.QUOTA_FREE_TRY_ASSESSMENTS:
                return this.quota_free_try_assessments
            case TenantServiceKey.QUOTA_USERS:
                return this.quota_users
            case TenantServiceKey.QUOTA_USERS_QMS:
                return this.quota_users_qms
            case TenantServiceKey.QUOTA_USERS_TD:
                return this.quota_users_td
            default:
                return null
        }
    }
}

export class TenantSubscriptionDataQuota {
    @Expose()
    public item_id: string
    @Expose()
    public price_id: string
    @Expose()
    public product_id: string
    @Expose()
    public quantity: number
    @Expose()
    public quota: number
    @Expose()
    public service: TenantServiceKey
}

export class TenantSubscriptionData {
    @Expose()
    public subscription_id: string
    @Expose()
    public status: TenantSubscriptionStatus | string
    @Expose()
    @Transform(anyToDate)
    public current_period_start: Date
    @Expose()
    @Transform(anyToDate)
    public current_period_end: Date
    @Expose()
    @Transform(anyToDate)
    public canceled_at: Date | undefined
    @Expose()
    @Transform(anyToDate)
    public ended_at: Date | undefined
    @Expose()
    @Transform(forceArray)
    @Type(() => TenantSubscriptionDataQuota)
    public quotas: TenantSubscriptionDataQuota[]

    public isActive(): boolean {
        return this.status === TenantSubscriptionStatus.ACTIVE
    }

    public requiresAttention(): boolean {
        const status = this.status as TenantSubscriptionStatus
        return !this.isActive() &&
            status !== TenantSubscriptionStatus.TRIALING &&
            status !== TenantSubscriptionStatus.CANCELED
    }

    public hasPaymentIssue(): boolean {
        const status = this.status as TenantSubscriptionStatus
        return status === TenantSubscriptionStatus.PAST_DUE ||
            status === TenantSubscriptionStatus.UNPAID
    }

    public needsPaymentMethod(): boolean {
        const status = this.status as TenantSubscriptionStatus
        return status === TenantSubscriptionStatus.PAUSED ||
            status === TenantSubscriptionStatus.INCOMPLETE_EXPIRED ||
            status === TenantSubscriptionStatus.INCOMPLETE
    }

    public isTrialing(): boolean {
        return this.status === TenantSubscriptionStatus.TRIALING
    }

    public isTerminal(): boolean {
        return this.status === TenantSubscriptionStatus.CANCELED
    }

    public isIncompleteExpired(): boolean {
        return this.status === TenantSubscriptionStatus.INCOMPLETE_EXPIRED
    }

    public getStatusSeverity(): 'success' | 'info' | 'warn' | 'error' {
        const status = this.status as TenantSubscriptionStatus
        switch (status) {
            case TenantSubscriptionStatus.ACTIVE:
                return 'success'
            case TenantSubscriptionStatus.TRIALING:
                return 'info'
            case TenantSubscriptionStatus.INCOMPLETE:
            case TenantSubscriptionStatus.PAUSED:
                return 'warn'
            case TenantSubscriptionStatus.PAST_DUE:
            case TenantSubscriptionStatus.UNPAID:
            case TenantSubscriptionStatus.INCOMPLETE_EXPIRED:
                return 'error'
            case TenantSubscriptionStatus.CANCELED:
                return 'warn'
            default:
                return 'warn'
        }
    }

    public getStatusMessageKey(): string {
        const status = this.status as TenantSubscriptionStatus
        switch (status) {
            case TenantSubscriptionStatus.ACTIVE:
                return 'billing.subscription.description.active'
            case TenantSubscriptionStatus.TRIALING:
                return 'billing.subscription.description.trialing'
            case TenantSubscriptionStatus.INCOMPLETE:
                return 'billing.subscription.description.incomplete'
            case TenantSubscriptionStatus.INCOMPLETE_EXPIRED:
                return 'billing.subscription.description.incomplete_expired'
            case TenantSubscriptionStatus.PAST_DUE:
                return 'billing.subscription.description.past_due'
            case TenantSubscriptionStatus.UNPAID:
                return 'billing.subscription.description.unpaid'
            case TenantSubscriptionStatus.PAUSED:
                return 'billing.subscription.description.paused'
            case TenantSubscriptionStatus.CANCELED:
                return 'billing.subscription.description.canceled'
            default:
                return 'billing.subscription.description.active'
        }
    }

    public getActionRequired(): 'update_payment' | 'add_payment' | 'authenticate' | 'manage' | null {
        const status = this.status as TenantSubscriptionStatus
        switch (status) {
            case TenantSubscriptionStatus.PAST_DUE:
            case TenantSubscriptionStatus.UNPAID:
                return 'update_payment'
            case TenantSubscriptionStatus.PAUSED:
            case TenantSubscriptionStatus.INCOMPLETE_EXPIRED:
                return 'add_payment'
            case TenantSubscriptionStatus.INCOMPLETE:
                return 'authenticate' // Could also be 'add_payment', but 'authenticate' covers 3DS case
            case TenantSubscriptionStatus.CANCELED:
            case TenantSubscriptionStatus.TRIALING:
                return null
            default:
                return 'manage'
        }
    }
}

export class Tenant extends _FirebaseDocument {
    @Expose()
    public name: string
    @Expose()
    public plan: TenantPlan | string | null = null
    @Expose()
    public stripeId: string | null = null
    @Expose()
    @Transform(forceArray)
    public members: string[] = []
    @Expose()
    @Transform(forceArray)
    public admins: string[] = []
    @Expose()
    @Transform(forceArray)
    public invited: string[] = []
    @Expose()
    @Type(() => TenantServices)
    public services: TenantServices = new TenantServices()
    @Expose()
    @Type(() => TenantSubscriptionData)
    public subscription_data: TenantSubscriptionData | null = null
    // Credit plan features (null for legacy plans)
    @Expose()
    public plan_features: TenantPlanFeatures | null = null
    @Expose()
    public welcome_bonus_granted: boolean = false
    // MYAC-837: generic per-tenant feature-flag overrides, resolved centrally in
    // featureFlagsStore as `effective(flag) = tenantOverride ?? global ?? default`.
    // Plain string keys; absent/null → defer to global, explicit false → force-off.
    // Replaces the one-off `self_serve_topup_allowed` (nothing wrote it, so no migration).
    @Expose()
    public feature_flags?: Record<string, boolean>
    // URL to the Stripe customer portal
    @Expose()
    public customer_portal_url: string | null = null
    // Trigger to request an update of the customer portal URL
    @Expose()
    public trigger_customer_portal_update: TenantCustomerPortalUpdateStatus | null = null
    // Public download URL of the company logo (Firebase Storage), or null (MYAC-866)
    @Expose()
    public logo_url: string | null = null
    // The language MAC writes this company's analyses and reports in (MYAC-1323).
    // Absent on companies created before it existed: they never chose, and runs
    // then follow the language of whoever starts them.
    @Expose()
    public locale: PreferredLanguage | null = null

    collection(): string {
        return "tenants";
    }
    public get hasAssessmentsRemaining(): boolean {
        const one_time_assessments = this.services.quota_one_time_assessments.quota_remaining || 0
        const recurrent_assessments = this.services.quota_recurrent_assessments.quota_remaining || 0
        const free_try_assessments = this.services.quota_free_try_assessments.quota_remaining || 0
        const total_assessments = one_time_assessments + recurrent_assessments + free_try_assessments
        return total_assessments ? total_assessments > 0 : false
    }

    public get subscriptionStatusInfo(): {
        status: TenantSubscriptionStatus | string | null
        severity: 'success' | 'info' | 'warn' | 'error'
        messageKey: string
        actionRequired: 'update_payment' | 'add_payment' | 'authenticate' | 'manage' | null
        requiresAttention: boolean
    } | null {
        if (!this.subscription_data) return null
        return {
            status: this.subscription_data.status,
            severity: this.subscription_data.getStatusSeverity(),
            messageKey: this.subscription_data.getStatusMessageKey(),
            actionRequired: this.subscription_data.getActionRequired(),
            requiresAttention: this.subscription_data.requiresAttention(),
        }
    }

    static fromFirebase(o: DocumentSnapshot): Tenant | null {
        return _FirebaseDocument._fromFirebase<Tenant>(Tenant, o);
    }
}

export class TenantQuotaLog extends _FirebaseDocument {
    @Expose()
    public tenant_id: string
    @Expose()
    public service: TenantServiceKey | string
    @Expose()
    public change: number
    @Expose()
    public previous: number
    @Expose()
    public next: number

    collection(): string {
        return `tenants/${this.tenant_id}/quota_logs`;
    }

    static fromFirebase(o: DocumentSnapshot): TenantQuotaLog | null {
        return _FirebaseDocument._fromFirebase<TenantQuotaLog>(TenantQuotaLog, o);
    }
}
